/**
 * EnemyAI.ts — Enemy decision-making for tactical combat.
 *
 * Each behavior type has a different action tendency:
 * - aggressive: prefers attacking, rarely defends
 * - defensive: prefers defending, attacks cautiously
 * - balanced: adapts to stance
 * - cowardly: flees when low HP
 * - boss: multi-phase (aggressive → defensive → aggressive)
 */

import type { CombatActionType, EnemyBehavior, CombatantState } from './CombatTypes';

/**
 * Determine enemy action based on behavior type and current combat state.
 * Returns the action type the enemy chooses.
 */
export function getEnemyAction(
  behavior: EnemyBehavior,
  enemyState: CombatantState,
  playerState: CombatantState,
  round: number,
): CombatActionType {
  const hpRatio = enemyState.hp / enemyState.hpMax;

  switch (behavior) {
    case 'aggressive':
      return aggressiveAI(hpRatio, enemyState.stance);

    case 'defensive':
      return defensiveAI(hpRatio, enemyState.stance);

    case 'balanced':
      return balancedAI(hpRatio, enemyState.stance, playerState.stance);

    case 'cowardly':
      return cowardlyAI(hpRatio, enemyState.stance);

    case 'boss':
      return bossAI(hpRatio, enemyState.stance, round);
  }
}

// ─── Behavior Implementations ───────────────────────────────────

function aggressiveAI(hpRatio: number, stance: string): CombatActionType {
  // Aggressive: 70% attack, 20% ability, 10% defend
  // If cornered, more likely to attack desperately
  if (stance === 'cornered') return 'attack';
  if (hpRatio < 0.2) return 'attack'; // fight to the death

  const roll = Math.random();
  if (roll < 0.7) return 'attack';
  if (roll < 0.9) return 'attack'; // "ability" for enemies is just a stronger attack
  return 'defend';
}

function defensiveAI(hpRatio: number, stance: string): CombatActionType {
  // Defensive: 40% defend, 40% attack, 20% based on stance
  if (stance === 'pressing' || stance === 'advantage') return 'attack'; // capitalize
  if (hpRatio < 0.3) return 'defend';

  const roll = Math.random();
  if (roll < 0.4) return 'defend';
  if (roll < 0.8) return 'attack';
  return 'defend';
}

function balancedAI(
  hpRatio: number,
  ownStance: string,
  _playerStance: string,
): CombatActionType {
  // Balanced: adapts to situation
  if (hpRatio < 0.3) return 'defend';
  if (ownStance === 'pressing' || ownStance === 'advantage') return 'attack';
  if (ownStance === 'cornered') return 'defend';

  const roll = Math.random();
  if (roll < 0.5) return 'attack';
  return 'defend';
}

function cowardlyAI(hpRatio: number, stance: string): CombatActionType {
  // Cowardly: tries to flee when hurt
  if (hpRatio < 0.4) return 'flee';
  if (hpRatio < 0.6 && stance === 'cornered') return 'flee';

  const roll = Math.random();
  if (roll < 0.5) return 'attack';
  if (roll < 0.7) return 'defend';
  return 'flee';
}

function bossAI(hpRatio: number, _stance: string, round: number): CombatActionType {
  // Boss: multi-phase behavior
  // Phase 1 (>60% HP): aggressive
  // Phase 2 (30-60% HP): defensive + special attacks
  // Phase 3 (<30% HP): enraged — all-out attack
  if (hpRatio > 0.6) {
    // Phase 1: aggressive
    return round % 3 === 0 ? 'defend' : 'attack';
  }
  if (hpRatio > 0.3) {
    // Phase 2: defensive pattern
    return round % 2 === 0 ? 'defend' : 'attack';
  }
  // Phase 3: enraged
  return 'attack';
}
